import React, { useState } from 'react';
import { changeAdminCredentials } from '../services/api';
import { useAuth } from '../context/AuthContext';
import './AdminSecurity.css';

export default function AdminSecurity() {
  const { admin, updateAdminSession } = useAuth();
  const [form, setForm] = useState({
    currentPassword: '',
    newUsername: admin?.username || '',
    newPassword: '',
    confirmPassword: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleChange = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (form.newPassword && form.newPassword !== form.confirmPassword) {
      setError('New password and confirmation must match.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        currentPassword: form.currentPassword,
        newUsername: form.newUsername,
        newPassword: form.newPassword || undefined,
      };

      const { data } = await changeAdminCredentials(payload);
      updateAdminSession(data.token, data.username);
      setSuccess('Admin login updated. Use the new username/password the next time you sign in.');
      setForm((current) => ({
        ...current,
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
        newUsername: data.username,
      }));
    } catch (err) {
      setError(err.response?.data?.error || 'Could not update admin login.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container" style={{ padding: '2rem 1.5rem', maxWidth: '840px' }}>
      <div className="page-header">
        <h1>Admin Security</h1>
        <p>Change the admin username or password from inside the dashboard whenever you need to.</p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="security-layout">
        <form className="card security-card" onSubmit={handleSubmit}>
          <div className="security-card-head">
            <div>
              <span className="security-kicker">Login control</span>
              <h2>Update admin credentials</h2>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Current username</label>
            <input className="form-control" value={admin?.username || ''} disabled />
          </div>

          <div className="form-group">
            <label className="form-label">New username</label>
            <input
              className="form-control"
              value={form.newUsername}
              onChange={(e) => handleChange('newUsername', e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Current password</label>
            <input
              className="form-control"
              type="password"
              value={form.currentPassword}
              onChange={(e) => handleChange('currentPassword', e.target.value)}
              required
            />
          </div>

          <div className="form-row-2">
            <div className="form-group">
              <label className="form-label">New password</label>
              <input
                className="form-control"
                type="password"
                value={form.newPassword}
                onChange={(e) => handleChange('newPassword', e.target.value)}
                placeholder="Leave blank to keep the current password"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Confirm new password</label>
              <input
                className="form-control"
                type="password"
                value={form.confirmPassword}
                onChange={(e) => handleChange('confirmPassword', e.target.value)}
                placeholder="Repeat the new password"
              />
            </div>
          </div>

          <div className="security-actions">
            <button className="btn btn-primary" type="submit" disabled={saving}>
              {saving ? 'Saving changes...' : 'Save admin login'}
            </button>
            <p>You’ll stay signed in after saving. Next login will use the updated credentials.</p>
          </div>
        </form>

        <div className="card security-sidecard">
          <span className="security-kicker">Emergency access</span>
          <h2>Locked out?</h2>
          <p>
            Keep your Railway <code>SETUP_KEY</code> private. If you ever lose the admin password again,
            that key can be used to reset the admin login from the backend without touching the database manually.
          </p>
          <ul className="security-list">
            <li>Use a long password you don’t reuse elsewhere.</li>
            <li>Rotate the <code>SETUP_KEY</code> if it has ever been exposed.</li>
            <li>Only share live admin credentials with people you trust to manage orders and customer data.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
